import type { RefObject } from "react";
import React from "react";
import { GanttViewMode, REQUEST_TASK_TYPES, TaskType } from "./prop-types";
import { Link } from "@procore/core-react";
import { EllipsisVertical } from "@procore/core-icons";
import type { GanttTearsheetRef } from "./gantt-tearsheets";
import type { BryntumGantt } from "@bryntum/gantt-react-thin";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { handleProjectTearsheetCallback } from "./gantt-tearsheet-callbacks";

export const taskRenderer = ({
   record,
   cellElement,
   ganttViewMode,
   canViewPeople,
   canViewProject,
   personDetailsTearsheetRef,
   projectDetailsTearsheetRef,
   ganttRef,
   expandedTasks,
}: {
   record: any;
   cellElement: any;
   ganttViewMode: GanttViewMode;
   canViewPeople: boolean;
   canViewProject: boolean;
   personDetailsTearsheetRef: RefObject<GanttTearsheetRef>;
   projectDetailsTearsheetRef: RefObject<GanttTearsheetRef>;
   ganttRef: RefObject<BryntumGantt>;
   expandedTasks: any;
}) => {
   if (!cellElement) return;
   const taskType = record?.getData("type");
   const emptyClassName = "no-children";

   // Doesn't render REQUEST tasks in the Resources view
   if (ganttViewMode === GanttViewMode.RESOURCES && REQUEST_TASK_TYPES.has(taskType)) {
      return;
   }
   if (
      [TaskType.PROJECT, TaskType.CATEGORY, TaskType.SUBCATEGORY].includes(taskType) &&
      (record.children == undefined || (record.children as any[]).length === 0)
   ) {
      cellElement.classList.add(emptyClassName);
   }

   const onEllipsisClick = (event: any) => {
      cellElement.dispatchEvent(
         new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: event.clientX, // Pass the same mouse position
            clientY: event.clientY,
         }),
      );
   };

   cellElement.setAttribute("data-task-type", taskType);

   //simplified the condition to check if the taskType is not a project
   //this will be rendering the cells for assignments(labor, equipment), requests, categories, subcategories
   if (taskType !== TaskType.PROJECT) {
      cellElement.classList.add("gantt-grid-row-ellipsis__container");
      if (taskType === TaskType.ASSIGNMENT) {
         return (
            <GanttAssignmentCell
               record={record}
               canViewPeople={canViewPeople}
               personDetailsTearsheetRef={personDetailsTearsheetRef}
               onEllipsisClick={onEllipsisClick}
            />
         );
      }
      return <GanttCell record={record} taskType={taskType} onEllipsisClick={onEllipsisClick} />;
   }
   // this will render the project name cell
   return (
      <GanttProjectNameCell
         record={record}
         canViewProject={canViewProject}
         projectDetailsTearsheetRef={projectDetailsTearsheetRef}
         ganttRef={ganttRef}
         expandedTasks={expandedTasks}
         onEllipsisClick={onEllipsisClick}
      />
   );
};

export const GanttProjectNameCell = (props: {
   record: any;
   canViewProject: boolean;
   projectDetailsTearsheetRef: RefObject<GanttTearsheetRef>;
   ganttRef: RefObject<BryntumGantt>;
   expandedTasks: any;
   onEllipsisClick: (event: any) => void;
}) => {
   const {
      record,
      canViewProject,
      projectDetailsTearsheetRef,
      ganttRef,
      expandedTasks,
      onEllipsisClick,
   } = props;
   const { groupId } = useGroupContext();
   return (
      <div
         style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            justifyContent: "space-between",
         }}
      >
         <div
            style={{
               overflow: "hidden",
               textOverflow: "ellipsis",
            }}
         >
            {canViewProject ? (
               <Link
                  onClick={() => {
                     const projectId = String(record.id);
                     projectDetailsTearsheetRef.current?.handleOpenTearsheet({
                        projectId,
                        callback: (data: any) =>
                           handleProjectTearsheetCallback(
                              data,
                              ganttRef as any,
                              expandedTasks,
                              groupId,
                           ),
                     });
                  }}
                  className="gantt-task-name-link"
                  data-testid="gantt-task-name-link"
                  aria-disabled={!canViewProject}
               >
                  {record?.getData("name")}
               </Link>
            ) : (
               <span>{record?.getData("name")}</span>
            )}
         </div>
         <EllipsisVertical
            size="sm"
            className="gantt-grid-row-ellipsis__project"
            data-testid="gantt-grid-row-ellipsis"
            onClick={onEllipsisClick}
         />
      </div>
   );
};

const GanttAssignmentCell = (props: {
   canViewPeople: boolean;
   personDetailsTearsheetRef: RefObject<GanttTearsheetRef>;
   onEllipsisClick: (event: any) => void;
   record: any;
}) => {
   const { record, canViewPeople, personDetailsTearsheetRef, onEllipsisClick } = props;

   return (
      <div
         style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            justifyContent: "space-between",
            alignItems: "center",
         }}
      >
         <span
            style={{
               overflow: "hidden",
               textOverflow: "ellipsis",
            }}
         >
            {canViewPeople ? (
               <Link
                  onClick={() => {
                     const personId = String(record?.getData("resourceId"));
                     personDetailsTearsheetRef.current?.handleOpenTearsheet({
                        projectId: "",
                        personId,
                        callback: () => {},
                     });
                  }}
                  className="gantt-task-name-link"
                  data-testid="gantt-person-name"
                  aria-disabled={!canViewPeople}
               >
                  {record?.getData("name")}
               </Link>
            ) : (
               <span>{record?.getData("name")}</span>
            )}
         </span>
         <EllipsisVertical
            size="sm"
            className="gantt-grid-row-ellipsis__resource"
            onClick={onEllipsisClick}
         />
      </div>
   );
};

const GanttCell = (props: {
   taskType: TaskType;
   record: any;
   onEllipsisClick: (event: any) => void;
}) => {
   const { taskType, record, onEllipsisClick } = props;
   return (
      <div
         style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            justifyContent: "space-between",
            alignItems: "center",
         }}
      >
         <span
            style={{
               overflow: "hidden",
               textOverflow: "ellipsis",
            }}
         >
            {REQUEST_TASK_TYPES.has(taskType) ? (
               <div className="requestPill">{record?.getData("name")}</div>
            ) : (
               record?.getData("name")
            )}
         </span>
         <EllipsisVertical
            size="sm"
            className="gantt-grid-row-ellipsis__resource"
            onClick={onEllipsisClick}
         />
      </div>
   );
};
